/**
 * One demand's risk, opened out.
 *
 * The board row carries a dollar figure and a basis; this is where the four
 * components behind it are readable separately, because they fail separately.
 * Budget consumed and calendar elapsed are shown as TWO figures rather than
 * the single "points ahead of schedule" number: a demand at 1942% of budget
 * and 73% of its calendar is a different problem from one at 110% and 100%,
 * and the combined figure collapses both into "ahead".
 *
 * Every panel prints the assumption the engine states for it. The projection
 * is a straight-line extrapolation of past burn and the schedule compares
 * spend against elapsed TIME, not against work completed -- neither is a fact
 * about the project, and a reader who takes them for one is over-confident in
 * exactly the direction that costs money.
 */
import { Link } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import type { ReactNode } from "react";
import { DrillInDialog } from "@/components/profitability/DrillInDialog";
import {
  PerPeriodSourceCell,
  PerPeriodStatusBadge,
} from "@/components/profitability/PerPeriodBudgetBadge";
import { BillingTypeCell, RiskBasisBadge } from "@/components/profitability/RiskBasisBadge";
import { hours as formatHours, money, monthLabel, percent, rate } from "@/lib/format";
import type { RiskNotAssessableRow, RiskPerPeriodRow, RiskRow } from "@/lib/profitability-api";

/** Any bucket can be focused: the not-assessable rows have no figures. */
export type RiskFocus = RiskRow | RiskNotAssessableRow | RiskPerPeriodRow;

function isRanked(row: RiskFocus): row is RiskRow {
  return "dollars_at_risk_basis" in row;
}

/**
 * A per-period row is the one bucket with no lifetime budget at all, so it is
 * identified by what it HAS -- the monthly reading -- rather than by the
 * absence of the ranked row's fields.
 */
function isPerPeriod(row: RiskFocus): row is RiskPerPeriodRow {
  return "monthly" in row;
}

/**
 * The engine's status codes, in English.
 *
 * These are machine values -- `no_end_date`, `in_progress` -- and printing
 * them raw made a panel about a missing date look like a stack trace. An
 * unmapped value falls through unchanged rather than disappearing, so a new
 * status from the service shows up as something to map instead of vanishing.
 */
const STATUS_LABEL: Record<string, string> = {
  ok: "measured",
  projected: "projected",
  in_progress: "in progress",
  ended: "calendar ended",
  no_end_date: "no end date",
  no_budget: "no budget set",
  no_start_date: "no start date",
  unknown: "not enough history",
};

function statusLabel(status: string | undefined): string | undefined {
  if (!status) return undefined;
  return STATUS_LABEL[status] ?? status.replace(/_/g, " ");
}

function Panel({
  title,
  status,
  assumption,
  reason,
  children,
}: {
  title: string;
  status?: string;
  assumption?: string;
  reason?: string | null;
  children?: ReactNode;
}) {
  const label = statusLabel(status);
  return (
    <section className="rounded-lg border p-3">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold">{title}</h3>
        {label ? <span className="text-xs text-muted-foreground">{label}</span> : null}
      </div>
      {children ? <div className="mt-2">{children}</div> : null}
      {reason ? <p className="mt-2 text-xs text-muted-foreground">{reason}</p> : null}
      {/* The assumption sits with the figure it qualifies, not in a page
          footnote -- a reader who scrolls past a footnote still acts on the
          number, and this is the number they act on. */}
      {assumption ? (
        <p className="mt-1 text-xs italic text-muted-foreground">{assumption}</p>
      ) : null}
    </section>
  );
}

function Figure({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold tabular-nums">{value}</p>
      {note ? <p className="text-xs text-muted-foreground">{note}</p> : null}
    </div>
  );
}

export function RiskDrillInDialog({
  row,
  range,
  onOpenChange,
}: {
  row: RiskFocus | null;
  range?: string;
  onOpenChange: (open: boolean) => void;
}) {
  const ranked = row && isRanked(row) ? row : null;
  const perPeriod = row && isPerPeriod(row) ? row : null;
  const monthly = perPeriod?.monthly;
  const schedule = row?.schedule;
  const budget = schedule?.budget;
  const calendar = schedule?.schedule;
  const projection = ranked?.projection;
  // A per-period demand carries a burn trend too, and it is the same question
  // there as anywhere: is the monthly rate rising? Reading it off whichever
  // bucket supplied the row keeps the panel from disappearing on this one.
  const trend = ranked?.trend ?? perPeriod?.trend;
  const pastEnd = row?.past_end;

  const peak = trend ? Math.max(1, ...trend.months.map((m) => m.hours)) : 1;
  const costedPeak = monthly ? Math.max(1, ...monthly.costed_months.map((m) => m.hours)) : 1;
  // Only label the axis when there are two ends to label. The tsconfig has
  // noUncheckedIndexedAccess, so the bounds are taken explicitly rather than
  // indexed and assumed.
  const costedRun = monthly && monthly.costed_months.length > 1 ? monthly.costed_months : [];
  const firstCostedMonth = costedRun[0];
  const lastCostedMonth = costedRun[costedRun.length - 1];

  return (
    <DrillInDialog
      open={row !== null}
      onOpenChange={onOpenChange}
      title={
        row
          ? // The client first, then the SOW. The demand name alone is
            // paperwork; the account is the relationship somebody has to go
            // and have a conversation about.
            `${row.account_name ? `${row.account_name} · ` : ""}${row.demand_name ?? row.demand_id}`
          : ""
      }
      description={
        ranked
          ? ranked.dollars_at_risk === null
            ? `${formatHours(ranked.exposure_hours ?? 0)} h of exposure, not priced -- this demand has no blended cost rate.`
            : `${money(ranked.dollars_at_risk)} of cost exposure${
                ranked.cost_per_hour ? ` at ${rate(ranked.cost_per_hour)}` : ""
              }`
          : // Never a dollar figure on a per-period demand, and never a total:
            // the only quantity this budget supports is a rate per month.
            monthly
            ? `${formatHours(monthly.mean_actual_hours_per_month ?? 0)} h a month against a ${formatHours(
                monthly.budget_hours_per_period ?? 0,
              )} h monthly budget. No lifetime overrun and no dollars at risk.`
            : (row?.reason ?? undefined)
      }
      action={
        row ? (
          <Link
            to="/dashboard/projects/$demandId"
            params={{ demandId: row.demand_id }}
            search={{ range }}
            onClick={() => onOpenChange(false)}
            className="inline-flex items-center gap-1 text-sm font-medium hover:underline"
          >
            Open project
            <ExternalLink className="size-3.5" aria-hidden />
          </Link>
        ) : null
      }
    >
      {row ? (
        <div className="space-y-3">
          {ranked ? (
            <div className="flex flex-wrap items-center gap-2">
              <RiskBasisBadge basis={ranked.dollars_at_risk_basis} />
              <BillingTypeCell billingType={ranked.billing_type} />
              {!ranked.actionable ? (
                <span className="text-xs text-muted-foreground">
                  The scheduled calendar has already ended -- this is realised, not a warning.
                </span>
              ) : null}
            </div>
          ) : null}

          {monthly && perPeriod ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <PerPeriodStatusBadge status={monthly.status} />
                <BillingTypeCell billingType={perPeriod.billing_type} />
                <PerPeriodSourceCell
                  source={perPeriod.budget_source}
                  evidence={perPeriod.per_period_evidence}
                />
              </div>

              <Panel title="Monthly budget" reason={monthly.reason} assumption={monthly.assumption}>
                {/* Three rates and no totals. The budget is an allowance per
                    month, so a "budget consumed" percentage of the kind the
                    Schedule panel below shows would be meaningless here -- it
                    is the figure that produced a $360,517 exposure on a demand
                    running 28 hours a month over. */}
                <div className="grid gap-4 sm:grid-cols-3">
                  <Figure
                    label="Monthly budget"
                    value={
                      monthly.budget_hours_per_period === null
                        ? "—"
                        : `${formatHours(monthly.budget_hours_per_period)} h`
                    }
                    note="per period, not for the life of the demand"
                  />
                  <Figure
                    label="Mean a month"
                    value={
                      monthly.mean_actual_hours_per_month === null
                        ? "—"
                        : `${formatHours(monthly.mean_actual_hours_per_month)} h`
                    }
                    note={
                      monthly.elapsed_months === null
                        ? undefined
                        : `${formatHours(monthly.actual_hours ?? 0)} h over ${
                            monthly.elapsed_months
                          } month${monthly.elapsed_months === 1 ? "" : "s"}`
                    }
                  />
                  <Figure
                    label="Against budget"
                    value={
                      monthly.vs_period_budget_hours_per_month === null
                        ? "—"
                        : `${monthly.vs_period_budget_hours_per_month > 0 ? "+" : ""}${formatHours(
                            monthly.vs_period_budget_hours_per_month,
                          )} h/mo`
                    }
                    note={
                      monthly.vs_period_budget_pct === null
                        ? undefined
                        : percent(monthly.vs_period_budget_pct)
                    }
                  />
                </div>
              </Panel>

              {monthly.costed_months.length > 0 ? (
                <Panel
                  title="Which months ran hot"
                  assumption={monthly.costed_months_assumption}
                  reason={`${monthly.costed_months_over_budget} of ${
                    monthly.costed_months_compared
                  } costed month${
                    monthly.costed_months_compared === 1 ? "" : "s"
                  } in this range went over the monthly budget.`}
                >
                  {/* Bars, and the assumption printed directly under them by the
                      Panel: this is the one place on the screen where someone
                      can see a row of monthly hours and start adding them up.
                      They are a different, lower series from the mean above --
                      a total taken here would disagree with every other hours
                      figure on the demand. */}
                  <div className="flex h-16 items-end gap-1">
                    {monthly.costed_months.map((month) => (
                      <div
                        key={month.period_id}
                        className={`flex-1 rounded-t ${month.over ? "bg-destructive/60" : "bg-primary/40"}`}
                        style={{ height: `${Math.max(2, (month.hours / costedPeak) * 100)}%` }}
                        title={`${monthLabel(month.period_start_date)} · ${formatHours(
                          month.hours,
                        )} h · ${
                          month.vs_period_budget_hours > 0 ? "+" : ""
                        }${formatHours(month.vs_period_budget_hours)} h against the monthly budget`}
                      />
                    ))}
                  </div>
                  {/* The ends, named. Every bar carries its month on hover, but
                      an unlabelled axis invites the reader to count positions,
                      and positions are not months here: a month with no costed
                      row is ABSENT, not zero, so the bars are the months that
                      exist rather than a continuous calendar. SEG Products has
                      no January 2026 entry at all. */}
                  {firstCostedMonth && lastCostedMonth ? (
                    <div className="mt-1 flex justify-between text-[11px] text-muted-foreground">
                      <span>{monthLabel(firstCostedMonth.period_start_date)}</span>
                      <span>{monthLabel(lastCostedMonth.period_start_date)}</span>
                    </div>
                  ) : null}
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Months with costed hours only -- a month with no cost line is not shown, so
                    these bars are not a continuous calendar.
                  </p>
                </Panel>
              ) : null}
            </>
          ) : null}

          <Panel
            title="Schedule"
            status={calendar?.status}
            // The engine's schedule sentence LEADS WITH THE LIFETIME FIGURE --
            // live it reads "1942.4% of budget hours consumed against 73.3% of
            // the calendar elapsed". Suppressing the number while printing the
            // prose that states it would leak the same misreading in words, and
            // the sentence cannot be split: both halves are in one clause. So
            // on a per-period demand the whole sentence goes and is replaced.
            // The calendar half survives in its own Figure below, with its own
            // note.
            reason={
              monthly
                ? "No spend-against-schedule sentence for a per-period budget: the engine's own reads the monthly allowance as a lifetime one, which is the comparison this bucket exists to refuse."
                : schedule?.reason
            }
            assumption={schedule?.assumption}
          >
            {/* Both components, side by side. The engine's single
                "points ahead of schedule" figure is shown too, but under them
                -- it is a difference of two numbers and is meaningless without
                them. */}
            <div className="grid gap-4 sm:grid-cols-2">
              {/* THE ONE FIGURE A PER-PERIOD DEMAND MUST NOT SHOW. On Yankee
                  Alliance the engine used to return 6,699.8% here -- the
                  monthly budget divided into lifetime hours, which is not a
                  proportion of anything, and the shape of the number that put
                  $360,517 against a demand running 28 hours a month over.

                  The engine no longer sends it: `schedule.budget` is ABSENT on
                  a per-period row, not null, so every read of it below is
                  optional-chained. This branch is kept as defence in depth
                  rather than deleted, because "not applicable" is a better
                  answer than a blank Figure, and because the guard costs
                  nothing if a future payload regresses. */}
              {monthly ? (
                <Figure
                  label="Budget consumed"
                  value="not applicable"
                  note="The budget is an allowance per month. Lifetime hours against it is not a percentage of anything -- see the monthly reading above."
                />
              ) : (
                <Figure
                  label="Budget consumed"
                  value={percent(budget?.consumed_pct ?? null)}
                  note={
                    budget?.budget_hours === null || budget?.budget_hours === undefined
                      ? (budget?.reason ?? "No budget set")
                      : `${formatHours(budget.actual_hours ?? 0)} h of ${formatHours(
                          budget.budget_hours,
                        )} h${budget.budget_source ? ` · from ${budget.budget_source}` : ""}`
                  }
                />
              )}
              <Figure
                label="Calendar elapsed"
                value={percent(calendar?.elapsed_pct ?? null)}
                note={
                  calendar?.elapsed_days === null || calendar?.elapsed_days === undefined
                    ? (calendar?.reason ?? "No end date")
                    : `${calendar.elapsed_days} of ${calendar.total_days} days · ${
                        calendar.start_date ?? "?"
                      } to ${calendar.end_date ?? "?"}`
                }
              />
            </div>
            {/* Suppressed on a per-period demand for the same reason: it is the
                lifetime consumed figure less the calendar, so it inherits the
                whole error. */}
            {!monthly &&
            schedule?.spend_vs_schedule_pts !== null &&
            schedule?.spend_vs_schedule_pts !== undefined ? (
              <p className="mt-2 text-sm">
                <span className="tabular-nums font-medium">
                  {schedule.spend_vs_schedule_pts.toLocaleString(undefined, {
                    maximumFractionDigits: 1,
                  })}
                </span>{" "}
                points of spend ahead of the calendar.
              </p>
            ) : null}
          </Panel>

          {projection ? (
            <Panel
              title="Projection"
              status={projection.status}
              reason={projection.reason}
              assumption={projection.assumption}
            >
              <div className="grid gap-4 sm:grid-cols-3">
                <Figure
                  label="Projected total"
                  value={
                    projection.projected_total_hours === null
                      ? "—"
                      : `${formatHours(projection.projected_total_hours)} h`
                  }
                  note={
                    projection.hours_per_day === null
                      ? undefined
                      : `${formatHours(projection.hours_per_day)} h/day observed`
                  }
                />
                <Figure
                  label="Against budget"
                  value={
                    projection.projected_vs_budget_hours === null
                      ? "—"
                      : `${projection.projected_vs_budget_hours > 0 ? "+" : ""}${formatHours(
                          projection.projected_vs_budget_hours,
                        )} h`
                  }
                  note={
                    projection.budget_hours === null
                      ? undefined
                      : `budget ${formatHours(projection.budget_hours)} h`
                  }
                />
                <Figure
                  label="Percent of budget"
                  value={percent(projection.projected_pct_of_budget)}
                />
              </div>
            </Panel>
          ) : null}

          {trend ? (
            <Panel
              title="Burn trend"
              status={trend.trend}
              reason={trend.reason}
              assumption={trend.assumption}
            >
              {trend.months.length > 0 ? (
                <>
                  {/* Bars rather than a sparkline: these are discrete closed
                      months, and a line between them implies a reading at
                      every point in between. */}
                  <div className="flex h-16 items-end gap-1">
                    {trend.months.map((month) => (
                      <div
                        key={month.period_id}
                        className="flex-1 rounded-t bg-primary/60"
                        style={{ height: `${Math.max(2, (month.hours / peak) * 100)}%` }}
                        title={`${formatHours(month.hours)} h`}
                      />
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {trend.months.length} closed month
                    {trend.months.length === 1 ? "" : "s"}, oldest first ·{" "}
                    {trend.earlier_mean_hours === null
                      ? "no earlier mean"
                      : `${formatHours(trend.earlier_mean_hours)} h/month then`}{" "}
                    →{" "}
                    {trend.recent_mean_hours === null
                      ? "no recent mean"
                      : `${formatHours(trend.recent_mean_hours)} h/month now`}
                    {trend.change_pct === null ? "" : ` (${percent(trend.change_pct)})`}
                  </p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">No closed months in the window.</p>
              )}
            </Panel>
          ) : null}

          <Panel
            title="Past the end date"
            status={pastEnd?.status}
            reason={pastEnd?.reason}
            assumption={pastEnd?.assumption}
          >
            <div className="grid gap-4 sm:grid-cols-3">
              <Figure
                label="Hours past end"
                value={
                  pastEnd?.hours_past_end === null || pastEnd?.hours_past_end === undefined
                    ? "—"
                    : `${formatHours(pastEnd.hours_past_end)} h`
                }
              />
              <Figure
                label="Months past end"
                value={
                  pastEnd?.months_past_end === null || pastEnd?.months_past_end === undefined
                    ? "—"
                    : String(pastEnd.months_past_end)
                }
              />
              <Figure label="End date" value={pastEnd?.end_date ?? "none set"} />
            </div>
            {pastEnd?.undated_months ? (
              <p className="mt-2 text-xs text-muted-foreground">
                {pastEnd.undated_months} month{pastEnd.undated_months === 1 ? "" : "s"} of hours
                could not be dated and are not counted either way.
              </p>
            ) : null}
          </Panel>

          {/* The engine's own sentence, last. It restates the panels above in
              prose, which is what someone pastes into a message. */}
          <p className="text-xs text-muted-foreground">{row.reason}</p>
        </div>
      ) : null}
    </DrillInDialog>
  );
}
